import React, { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import AppShell from "shared/components/layout/AppShell";
import { UserProvider } from "features/admin/users/UserContext";
import { ModuleProvider } from "features/admin/modules/ModuleContext";
import { AdminProvider } from "features/admin/AdminContext";
import { ConnectProvider } from "features/chat/ConnectContext";
import { SignUpProvider } from "features/auth/SignUpContext";
import { TaskProvider } from "features/tasks/TaskContext";

// HOCs for route protection
import PrivateRoute from "app/routes/PrivateRoute";
import PublicRoute from "app/routes/PublicRoute";
import HomeRedirect from "app/routes/HomeRedirect";
import GlobalLoader from "./pages/GlobalLoader";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AccountProvider } from "features/admin/company/AccountContext";
import { TeamProvider } from "features/admin/teams/TeamContext";
import { CategoryProvider } from "features/admin/rooms/CategoryContext";
import { TagProvider } from "features/admin/tags/TagContext";
const TaskPropertiesHome = lazy(() => import("./features/tasks/TaskNewDesign/TaskPropertiesHome"));
const DailySalesHome = lazy(() => import("./features/reports/DailySalesHome"));

const RoomCategoryHome = lazy(() => import("features/admin/rooms/RoomCategoryHome"));
const ShareTeamTagsHome = lazy(() => import("features/admin/tags/ShareTeamTagsHome"));
const MergeCompanyHome = lazy(() => import("features/admin/company/MergeCompanyHome"));
const RollbackCompanyHome = lazy(() => import("features/admin/company/RollbackCompanyHome"));
const CreateAndEditReport = lazy(() => import("features/reports/CreateAndEditReport"));
const MuteNotifications = lazy(() => import("./pages/MuteNotifications"));
const UserProfilePage = lazy(() => import("features/user/UserProfilePage"));

const Reply = lazy(() => import("./components/connect/chat/Reply"));
const SharedFileUrl = lazy(() => import("./features/files/SharedFileUrl"));
const FileHubUrl = lazy(() => import("./features/files/FileHubUrl"));
const AccountSwitch = lazy(() => import("./components/connect/AccountSwitch"));
const Notifications = lazy(() => import("./components/Notifications"));

// Lazy-loaded route components (code-splitting)
const Login = lazy(() => import("features/auth/components/Login"));
const NotFound = lazy(() => import("./components/NotFound"));
const Connect = lazy(() => import("./pages/Connect"));
const ChatScreenContainer = lazy(() => import("./components/connect/chat/ChatScreenContainer"));
const AdminPanel = lazy(() => import("./features/admin/AdminPanel"));
const DailyReportPage = lazy(() => import("./features/reports/DailyReportPage"));
const SignupHome = lazy(() => import("features/auth/components/Signup/SignupHome"));
const CallingMeeting = lazy(() => import("./components/Call_window/Calling_meeting"));
const FileHubHome = lazy(() => import("./features/files/components/FileHubHome"));
const TasksDashboardPage = lazy(() => import("./pages/TasksDashboardPage"));
const TaskProperties = lazy(() => import("./features/tasks/TaskDetails/TaskProperties"));
const ModuleManageHome = lazy(() => import("features/admin/modules/ModuleManageHome"));
const TaskSettingsHome = lazy(() => import("features/admin/tasks/TaskSettingsHome"));
const CompanyManagementHome = lazy(() => import("features/admin/company/CompanyManagementHome"));
const UserManagementHome = lazy(() => import("features/admin/users/UserManagementHome"));
const TeamManagementHome = lazy(() => import("features/admin/teams/TeamManagementHome"));
const RoleManagementHome = lazy(() => import("features/admin/role/RoleManagementHome"));
const VenueManagementHome = lazy(() => import("features/admin/venue/VenueManagementHome"));
const OtpManagementHome = lazy(() => import("features/admin/otp/OtpManagementHome"));
const TaskCreateAndEdit = lazy(() => import("features/tasks/CreateEidtTask/TaskCreateAndEdit"));
const SubscriptionsBillingCon = lazy(() => import("features/admin/subscriptions/SubscriptionsBillingCon"));
const BusinessAccountInfo = lazy(() => import("features/admin/business/BusinessAccountInfo"));

export const router = createBrowserRouter([
    {
        path: '/',
        element: <HomeRedirect />
    },
    {
        path: "login",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <Login type={''} />
                </Suspense>
            </PublicRoute>
        ),
    },
    {
        path: "signin_with_otp",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <Login type={'signin_with_otp'} />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "/signin_with_otp/:short_url",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <Login type={'signin_with_otp'} />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "forgot-password",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <Login type={'forgot-password'} />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "signup",
        element: (
            <PublicRoute>
                <SignUpProvider>
                    <Suspense fallback={<GlobalLoader />}>
                        <SignupHome type="normal" />
                    </Suspense>
                </SignUpProvider>
            </PublicRoute>
        ),
    },
    {
        path: "/signup/:short_url",
        element: (
            <PublicRoute>
                <SignUpProvider>
                    <Suspense fallback={<GlobalLoader />}>
                        <SignupHome type="invitedUser" />
                    </Suspense>
                </SignUpProvider>
            </PublicRoute>
        ),
    },
    {
        path: "/connect",
        element: <PrivateRoute />, // single PrivateRoute
        children: [{
            // AppShell: mobile layout wrapper for all /connect routes
            element: <AppShell />,
            children: [
            {
                index: true,
                element: (
                    <ErrorBoundary>
                        <ConnectProvider>
                            <Suspense fallback={<GlobalLoader />}>
                                <Connect />
                            </Suspense>
                        </ConnectProvider>
                    </ErrorBoundary>
                ),
            },
            {
                path: ":slug",
                element: (
                    <ErrorBoundary>
                        <ConnectProvider>
                            <Suspense fallback={<GlobalLoader />}>
                                <ChatScreenContainer />
                            </Suspense>
                        </ConnectProvider>
                    </ErrorBoundary>
                ),
                children: [
                    {
                        path: "file-hub",
                        children: [
                            {
                                path: ":tab", // e.g. tag, files, links
                                element: (
                                    <ConnectProvider>
                                        <Suspense fallback={<GlobalLoader />}>
                                            <FileHubHome />
                                        </Suspense>
                                    </ConnectProvider>
                                ),
                            },
                            {
                                index: true,
                                element: (
                                    <ConnectProvider>
                                        <Suspense fallback={<GlobalLoader />}>
                                            <FileHubHome />
                                        </Suspense>
                                    </ConnectProvider>
                                ),
                            }
                        ]
                    },
                    {
                        path: "reply/:msg_id", // ✅ make this relative
                        element: (
                            <ConnectProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <Reply />
                                </Suspense>
                            </ConnectProvider>
                        ),
                    },
                    {
                        path: "filter/:filter_name",
                        element: <></>,
                        children: [
                            {
                                path: ":search_string",
                                element: <></>,
                            }
                        ]
                    },
                    {
                        path: "tasks",
                        element: (
                            <ConnectProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <TasksDashboardPage />
                                </Suspense>
                            </ConnectProvider>
                        ),
                        children: [
                            { path: "create-task", element: (<Suspense fallback={<GlobalLoader />}><TaskCreateAndEdit /></Suspense>) },
                            { path: "edit-task", element: (<Suspense fallback={<GlobalLoader />}><TaskCreateAndEdit /></Suspense>) },
                            {
                                path: ":task_id",
                                element: (
                                    <ErrorBoundary>
                                        <TaskProvider>
                                            <Suspense fallback={<GlobalLoader />}>
                                                <TaskPropertiesHome />
                                            </Suspense>
                                        </TaskProvider>
                                    </ErrorBoundary>
                                ),
                            }
                        ]
                    },
                    {
                        path: ":task_id",
                        element: (
                            <ErrorBoundary>
                                <TaskProvider>
                                    <Suspense fallback={<GlobalLoader />}>
                                        <TaskPropertiesHome />
                                    </Suspense>
                                </TaskProvider>
                            </ErrorBoundary>
                        ),
                    },
                ]
            },
            {
                path: "profile",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <UserProfilePage />
                    </Suspense>
                ),
            },
            {
                path: "daily-sales",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <DailySalesHome />
                    </Suspense>
                ),
            },
            {
                path: "notifications",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <Notifications />
                    </Suspense>
                ),
            },
            {
                path: "mute-notifications",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <MuteNotifications />
                    </Suspense>
                )
            },
            {
                path: "switch-account",
                element: (
                    <ConnectProvider>
                        <Suspense fallback={<GlobalLoader />}>
                            <AccountSwitch />
                        </Suspense>
                    </ConnectProvider>
                ),
            },
            // {
            //     path: "flagged-items",
            //     element: (
            //         <ConnectProvider>
            //             <Suspense fallback={<GlobalLoader />}>
            //                 <DashboardProvider>
            //                     <FlaggedItemsMain />
            //                 </DashboardProvider>
            //             </Suspense>
            //         </ConnectProvider>
            //     ),
            // },
            {
                path: "file-hub",
                children: [
                    {
                        path: ":tab", // e.g. tag, files, links
                        element: (
                            <ConnectProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <FileHubHome />
                                </Suspense>
                            </ConnectProvider>
                        ),
                    },
                    {
                        index: true,
                        element: (
                            <ConnectProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <FileHubHome />
                                </Suspense>
                            </ConnectProvider>
                        ),
                    }
                ]
            },
            // ...existing code...
            {
                path: "tasks",
                element: (
                    <ConnectProvider>
                        <Suspense fallback={<GlobalLoader />}>
                            <TasksDashboardPage />
                        </Suspense>
                    </ConnectProvider>
                ),
                children: [
                    { path: "create-task", element: <TaskCreateAndEdit /> },
                    { path: "edit-task", element: <TaskCreateAndEdit /> },
                    {
                        path: ":task_id",
                        element: (
                            <ErrorBoundary>
                                <TaskProvider>
                                    <Suspense fallback={<GlobalLoader />}>
                                        <TaskPropertiesHome />
                                    </Suspense>
                                </TaskProvider>
                            </ErrorBoundary>
                        ),
                    },
                    {
                        path: ":task_id/old",
                        element: (
                            <ErrorBoundary>
                                <TaskProvider>
                                    <Suspense fallback={<GlobalLoader />}>
                                        <TaskProperties/>
                                    </Suspense>
                                </TaskProvider>
                            </ErrorBoundary>
                        ),
                    }
                ]
            },
            {
                path: "daily-report",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <DailyReportPage />
                    </Suspense>
                ),
                children: [
                    { path: "create-reports", element: <Suspense fallback={<GlobalLoader />}><CreateAndEditReport /></Suspense> },
                    { path: "edit-report", element: <Suspense fallback={<GlobalLoader />}><CreateAndEditReport /></Suspense> }
                ]
            },
            {
                path: "adminpanel",
                element: (
                    <ErrorBoundary>
                        <AdminProvider>
                            <Suspense fallback={<GlobalLoader />}>
                                <AdminPanel />
                            </Suspense>
                        </AdminProvider>
                    </ErrorBoundary>
                ),
                children: [
                    { path: "subscription-management", element: <Suspense fallback={<GlobalLoader />}><SubscriptionsBillingCon /></Suspense> },
                    { path: "user-management", element: <UserProvider> <Suspense fallback={<GlobalLoader />}><UserManagementHome /> </Suspense></UserProvider> },
                    { path: "account-information", element: <AccountProvider> <Suspense fallback={<GlobalLoader />}><BusinessAccountInfo /></Suspense> </AccountProvider> },
                    { path: "otp-management", element: <Suspense fallback={<GlobalLoader />}><OtpManagementHome /></Suspense> },
                    { path: "team-management", element: <TeamProvider>  <Suspense fallback={<GlobalLoader />}><TeamManagementHome /> </Suspense> </TeamProvider> },
                    { path: "room-category", element: <CategoryProvider>  <Suspense fallback={<GlobalLoader />}><RoomCategoryHome /> </Suspense> </CategoryProvider> },
                    { path: "module-management", element: <ModuleProvider><Suspense fallback={<GlobalLoader />}><ModuleManageHome /></Suspense></ModuleProvider> },
                    { path: "task-settings", element: <ModuleProvider><Suspense fallback={<GlobalLoader />}><TaskSettingsHome /></Suspense></ModuleProvider> },
                    { path: "role-management", element: <Suspense fallback={<GlobalLoader />}><RoleManagementHome /></Suspense> },
                    { path: "shared-tags", element: <TagProvider> <Suspense fallback={<GlobalLoader />}><ShareTeamTagsHome /></Suspense></TagProvider> },
                    { path: "merge-company", element: <Suspense fallback={<GlobalLoader />}><MergeCompanyHome /></Suspense> },
                    { path: "rollback-company", element: <Suspense fallback={<GlobalLoader />}><RollbackCompanyHome /></Suspense> },
                    { path: "venue-management", element: <Suspense fallback={<GlobalLoader />}><VenueManagementHome /></Suspense> },
                    { path: "company-management", element: <Suspense fallback={<GlobalLoader />}><CompanyManagementHome /></Suspense> }
                ]
            }
            ] // end AppShell children
        }]  // end AppShell layout route
    },
    {
        path: "/f/:url",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <SharedFileUrl />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "/file",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <FileHubUrl />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "/call/:cid",
        element: (
            <ErrorBoundary>
                <Suspense fallback={<GlobalLoader />}>
                    <CallingMeeting />
                </Suspense>
            </ErrorBoundary>
        ),
    },
    {
        path: "/c/:id",
        element: (
            <ErrorBoundary>
                <Suspense fallback={<GlobalLoader />}>
                    <CallingMeeting />
                </Suspense>
            </ErrorBoundary>
        ),
    },
    {
        path: "/u/:id",
        element: (
            <ErrorBoundary>
                <Suspense fallback={<GlobalLoader />}>
                    <CallingMeeting />
                </Suspense>
            </ErrorBoundary>
        ),
    },
    {
        path: "*", // This wildcard route will match any undefined paths
        element: (
            <Suspense fallback={<GlobalLoader />}>
                <NotFound />
            </Suspense>
        ), // Render the NotFound component for unmatched routes
    },
])